import React, { useState, useEffect } from "react";
import {
  Box,
  Input,
  VStack,
  Text,
  Button,
  SimpleGrid,
  Flex,
  IconButton,
  useToast,
  Heading,
  HStack,
  useColorModeValue,
  Textarea,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Container,
  InputGroup,
  InputLeftElement,
  Icon,
  Card,
  CardBody,
  useDisclosure,
} from "@chakra-ui/react";
import { FiEdit3, FiSearch } from "react-icons/fi";
import {
  DashboardCategoryPanel,
  DashboardFolderCard,
  DashboardActionShell,
} from "../utils/DashboardCategoryPanel";
import {
  DashboardDataTable,
  useDashboardTableStyles,
} from "../utils/DashboardDataTable";
import { CheckIcon, CloseIcon, EditIcon } from "@chakra-ui/icons";
import { hitBeApi } from "../../../api/api";
import TabCacheButton from "../../common/TabCacheButton";
import { useNavigate } from "react-router-dom";
import DeleteButtonWithConfirmation from "../utils/DeleteConfirmation";
import ShareCollaborationModal from "../../common/ShareCollaborationModal";

const NoteComponent = () => {
  const toast = useToast();
  const shareFolderModal = useDisclosure();
  const [shareCategoryTarget, setShareCategoryTarget] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [subNotes, setSubNotes] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [categoryPath, setCategoryPath] = useState([]);
  const [isCategoryBeingSelected, setIsCategoryBeingSelected] = useState(false);

  // Define dynamic color mode values
  const hoverBg = useColorModeValue("gray.100", "gray.600");
  const headingNoteColor = useColorModeValue("blue.600", "blue.300");
  const inputBg = useColorModeValue("white", "gray.800");
  const placeholderColor = useColorModeValue("gray.500", "gray.400");
  const linkColor = useColorModeValue("blue.600", "blue.300");
  const breadcrumbCurrentBg = useColorModeValue("teal.100", "teal.800");
  const breadcrumbCurrentColor = useColorModeValue("teal.700", "teal.100");
  const breadcrumbLinkColor = useColorModeValue("gray.700", "gray.200");
  const breadcrumbLinkHoverBg = useColorModeValue("white", "gray.600");
  const categoryRowBg = useColorModeValue("white", "gray.800");
  const categoryRowHoverBg = useColorModeValue("gray.50", "gray.700");
  const categoryIconBgRoot = useColorModeValue("teal.50", "teal.900");
  const categoryIconBgNested = useColorModeValue("blue.50", "blue.900");
  const textColorCard = useColorModeValue("gray.800", "gray.100");
  const noteFieldGradient = useColorModeValue(
    "linear(to-r, blue.50, blue.100)",
    "linear(to-r, gray.700, gray.600)"
  );
  const tableStyles = useDashboardTableStyles();

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const surfaceCard = useColorModeValue("white", "gray.800");
  const mutedLine = useColorModeValue("gray.200", "gray.600");
  const subtextColor = useColorModeValue("gray.600", "gray.400");
  const searchPanelBg = useColorModeValue("white", "gray.800");
  const pathStripBg = useColorModeValue("gray.50", "gray.900");
  const searchBorder = useColorModeValue("teal.200", "teal.600");
  const searchFocusShadow = useColorModeValue(
    "0 0 0 1px var(--chakra-colors-teal-500)",
    "0 0 0 1px var(--chakra-colors-teal-300)"
  );

  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(null); // To track which category or Note is being edited
  const [editedCategoryName, setEditedCategoryName] = useState("");
  const [editedNoteTitle, setEditedNoteTitle] = useState("");
  const [editedNote, setEditedNote] = useState("");

  const handleStartEdit = (item, type) => {
    setIsEditing({ id: item._id, type });
    if (type === "category") {
      setEditedCategoryName(item.name);
    } else {
      setEditedNoteTitle(item.title);
      setEditedNote(item.note);
    }
  };

  // Fetch initial categories and Notes on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const categoryResponse = await hitBeApi("categories?type=note", "GET");

        if (categoryResponse.success) {
          setCategories(categoryResponse.data);
        }
      } catch (error) {
        if (error.message.includes("Unauthorized")) {
          navigate("/");
          document.cookie =
            "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
          toast({
            title: "Error",
            description: "Unauthorized: Invalid token, please login once",
            status: "error",
            duration: 2000,
            isClosable: true,
          });
          return;
        }
        toast({
          title: "Error",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle selecting a category
  const handleCategorySelect = async (
    category,
    isDirectClick = false,
    directClickIndex = 0
  ) => {
    if (isCategoryBeingSelected) {
      return;
    }
    try {
      setIsCategoryBeingSelected(true);
      const subCategoryResponse = await hitBeApi(
        `categories?parentId=${category._id}`,
        "GET"
      );
      const subNoteResponse = await hitBeApi(
        `notes?categoryId=${category._id}`,
        "GET"
      );
      setSelectedCategory(category);
      if (!isDirectClick) {
        setCategoryPath((categoryPath) => [...categoryPath, category]);
      } else {
        setCategoryPath((categoryPath) => [
          ...categoryPath.slice(0, directClickIndex + 1),
        ]);
      }

      if (subCategoryResponse.success) {
        setSubCategories(subCategoryResponse.data);
      }
      if (subNoteResponse.success) {
        setSubNotes(subNoteResponse.data);
      }
      setIsCategoryBeingSelected(false);
    } catch (error) {
      setIsCategoryBeingSelected(false);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSubCategories([]);
    setCategoryPath([]);
    setSubNotes([]);
  };

  const handleNoteCreate = async () => {
    if (!selectedCategory) {
      return;
    }
    if (newNote.length === 0 || newNoteTitle.length === 0) {
      return;
    }
    try {
      const createSubNoteResponse = await hitBeApi(`notes`, "POST", {
        note: newNote,
        categoryId: selectedCategory._id,
        title: newNoteTitle,
      });
      if (createSubNoteResponse.success) {
        setSubNotes([...subNotes, createSubNoteResponse.data]);
      }
      setNewNote("");
      setNewNoteTitle("");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleNoteUpdate = async (noteId, updatedNote, updatedNoteTitle) => {
    if (updatedNote.length === 0 || updatedNoteTitle.length === 0 || !noteId) {
      toast({
        title: "Error",
        description: "All fields are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const updateNoteResponse = await hitBeApi(`notes/${noteId}`, "PUT", {
        note: updatedNote,
        title: updatedNoteTitle,
      });

      if (updateNoteResponse.success) {
        if (selectedCategory) {
          const updatedSubNotes = subNotes.map((note) =>
            note._id === noteId
              ? { ...note, note: updatedNote, title: updatedNoteTitle }
              : note
          );
          setSubNotes(updatedSubNotes);
        }
        setIsEditing(null); // Exit editing mode

        toast({
          title: "Success",
          description: "Note updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleNoteDelete = async (noteId) => {
    if (!noteId) {
      toast({
        title: "Error",
        description: "Note ID is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const deleteNoteResponse = await hitBeApi(`notes/${noteId}`, "DELETE");

      if (deleteNoteResponse.success) {
        if (selectedCategory) {
          const updatedSubNotes = subNotes.filter(
            (note) => note._id !== noteId
          );
          setSubNotes(updatedSubNotes);
        }

        toast({
          title: "Success",
          description: "Note soft deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCategoryCreate = async () => {
    if (newCategory.length === 0) {
      return;
    }
    try {
      const createSubCategoryResponse = await hitBeApi(`categories`, "POST", {
        name: newCategory,
        type: "note",
        parentId: selectedCategory?._id,
      });
      if (createSubCategoryResponse.success) {
        if (selectedCategory) {
          setSubCategories([...subCategories, createSubCategoryResponse.data]);
        } else {
          setCategories([...categories, createSubCategoryResponse.data]);
        }
      }
      setNewCategory("");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCategoryUpdate = async (categoryId, updatedCategoryName) => {
    if (!categoryId || updatedCategoryName.length === 0) {
      toast({
        title: "Error",
        description: "Category ID and updated name are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const updateCategoryResponse = await hitBeApi(
        `categories/${categoryId}`,
        "PUT",
        {
          name: updatedCategoryName,
          parentId: selectedCategory?._id, // Optional: if updating parent category
        }
      );

      if (updateCategoryResponse.success) {
        if (selectedCategory) {
          // Update the category in the list
          const updatedSubCategories = subCategories.map((category) =>
            category._id === categoryId
              ? { ...category, name: updatedCategoryName }
              : category
          );
          setSubCategories(updatedSubCategories);
        } else {
          // Update the category in the list
          const updatedCategories = categories.map((category) =>
            category._id === categoryId
              ? { ...category, name: updatedCategoryName }
              : category
          );
          setCategories(updatedCategories);
        }
        setIsEditing(null); // Exit editing mode

        toast({
          title: "Success",
          description: "Category updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCategoryDelete = async (categoryId) => {
    if (!categoryId) {
      toast({
        title: "Error",
        description: "Category ID is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const deleteCategoryResponse = await hitBeApi(
        `categories/${categoryId}`,
        "DELETE"
      );

      if (deleteCategoryResponse.success) {
        if (selectedCategory) {
          // Filter out the deleted category from the categories list
          const updatedSubCategories = subCategories.filter(
            (category) => category._id !== categoryId
          );
          setSubCategories(updatedSubCategories);
        } else {
          // Filter out the deleted category from the categories list
          const updatedCategories = categories.filter(
            (category) => category._id !== categoryId
          );
          setCategories(updatedCategories);
        }

        toast({
          title: "Success",
          description: "Category soft deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Make API call to search Notes whenever searchTerm updates
  useEffect(() => {
    if (searchTerm && searchTerm.length > 0) {
      const fetchSearchResults = async () => {
        try {
          const response = await hitBeApi(`notes/${searchTerm}`, "GET", null);
          setSearchResults(response.data); // Update the state with search results

          // Show success toast
          if (
            (response.data.length > 0 && searchResults.length === 0) ||
            (response.data.length === 0 && searchResults.length > 0)
          ) {
            toast({
              title: "Search completed.",
              description: `Found ${response.data.length} result(s) for "${searchTerm}".`,
              status: "success",
              duration: 4000,
              isClosable: true,
            });
          }
        } catch (error) {
          console.error("Error searching Notes:", error);
          // Show error toast
          toast({
            title: "Search failed.",
            description: error.message,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
      };

      fetchSearchResults();
    } else {
      setSearchResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, toast]); // Include toast in the dependency array

  const handleChangeSearchTerm = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  };

  const NotesShareCollaborationModal = ShareCollaborationModal;
  const shareNotesCollaborationTitle = shareCategoryTarget
    ? `Share notes folder “${shareCategoryTarget.name}”`
    : "Share folder";
  const shareNotesCategoryAnchorId =
    shareCategoryTarget != null ? String(shareCategoryTarget._id) : undefined;

  return (
    <Box minH="100vh" bg={pageBg} py={{ base: 4, md: 8 }}>
      <Container maxW="container.xl" px={{ base: 3, md: 6 }}>
        <VStack spacing={{ base: 5, md: 7 }} align="stretch">
          <Box
            role="region"
            aria-label="Notes library"
            overflow="hidden"
            borderRadius="2xl"
            boxShadow="lg"
            borderWidth="1px"
            borderColor={mutedLine}
            bg={surfaceCard}
          >
            <Box
              w="100%"
              minH={{ base: "120px", md: "auto" }}
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 7 }}
              bg="purple.600"
              backgroundImage="linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)"
            >
              <Flex
                align="flex-start"
                justify="space-between"
                gap={4}
                direction={{ base: "column", sm: "row" }}
              >
                <Flex
                  align="flex-start"
                  gap={4}
                  direction={{ base: "column", sm: "row" }}
                  flex="1"
                  minW={0}
                >
                  <Flex
                    align="center"
                    justify="center"
                    w={12}
                    h={12}
                    borderRadius="xl"
                    bg="whiteAlpha.300"
                    flexShrink={0}
                    borderWidth="1px"
                    borderColor="whiteAlpha.400"
                  >
                    <Icon as={FiEdit3} boxSize={7} color="white" aria-hidden />
                  </Flex>
                  <Box textAlign="left" flex="1" minW={0}>
                    <Heading
                      as="h1"
                      size="lg"
                      color="white"
                      fontWeight="extrabold"
                      letterSpacing="tight"
                    >
                      Notes & links
                    </Heading>
                    <Text
                      fontSize="sm"
                      color="white"
                      opacity={0.95}
                      mt={2}
                      maxW="3xl"
                      lineHeight="tall"
                    >
                      Folder trees, searchable content, and quick edits — same structure as
                      Files and Portfolio categories. Titles and body text are stored per note.
                    </Text>
                  </Box>
                </Flex>
                <TabCacheButton tabId="notes" onGradient />
              </Flex>
            </Box>
          </Box>

          <Card
            borderRadius="xl"
            borderWidth="1px"
            borderColor={mutedLine}
            bg={surfaceCard}
            boxShadow="sm"
            overflow="hidden"
          >
            <CardBody py={{ base: 3, md: 4 }}>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none" h="100%">
                  <Icon as={FiSearch} color="gray.400" boxSize={5} aria-hidden />
                </InputLeftElement>
                <Input
                  pl={10}
                  placeholder="Search notes by title or content…"
                  value={searchTerm}
                  onChange={(e) => handleChangeSearchTerm(e.target.value)}
                  borderRadius="full"
                  shadow="sm"
                  bg={inputBg}
                  borderColor={searchBorder}
                  _placeholder={{ color: placeholderColor }}
                  _hover={{ bg: hoverBg, shadow: "sm" }}
                  _focus={{ borderColor: "teal.500", boxShadow: searchFocusShadow }}
                  transition="background 0.2s ease"
                />
              </InputGroup>
            </CardBody>
          </Card>

          {categoryPath?.length > 0 && (
            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderColor={mutedLine}
              bg={pathStripBg}
              boxShadow="sm"
              overflow="hidden"
            >
              <CardBody px={{ base: 3, lg: 4 }} py={3}>
                <HStack justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                  <Badge colorScheme="teal" px={2} py={1} borderRadius="md">
                    Current path
                  </Badge>
                  <Text fontSize="xs" color={subtextColor}>
                    Click any step to jump back
                  </Text>
                </HStack>
                <Breadcrumb separator="›" fontWeight="semibold" fontSize="sm">
                  {categoryPath.map((category, index) => {
                    const isLastItem = index === categoryPath.length - 1;
                    return (
                      <BreadcrumbItem key={category._id} isCurrentPage={isLastItem}>
                        <BreadcrumbLink
                          onClick={() => handleCategorySelect(category, true, index)}
                          px={3}
                          py={1}
                          borderRadius="full"
                          bg={isLastItem ? breadcrumbCurrentBg : "transparent"}
                          color={isLastItem ? breadcrumbCurrentColor : breadcrumbLinkColor}
                          _hover={{
                            textDecoration: "none",
                            bg: isLastItem ? breadcrumbCurrentBg : breadcrumbLinkHoverBg,
                          }}
                        >
                          {category.name}
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                    );
                  })}
                </Breadcrumb>
              </CardBody>
            </Card>
          )}

          {searchResults.length > 0 && (
            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderColor={mutedLine}
              bg={searchPanelBg}
              boxShadow="md"
              overflow="hidden"
            >
              <CardBody p={{ base: 4, md: 5 }}>
                <Flex align="center" justify="space-between" mb={4} gap={3} flexWrap="wrap">
                  <Heading size="md" color={headingNoteColor}>
                    Search results
                  </Heading>
                  <IconButton
                    icon={<CloseIcon />}
                    aria-label="Clear search results"
                    variant="outline"
                    colorScheme="red"
                    size="sm"
                    borderRadius="full"
                    onClick={() => {
                      setSearchResults([]);
                      setSearchTerm("");
                    }}
                  />
                </Flex>
                <VStack spacing={3} align="stretch" maxH={{ base: "50vh", md: "min(55vh, 400px)" }} overflowY="auto">
                  {searchResults.map((result) => (
                    <a
                      key={result._id}
                      href={result.note}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "block", width: "100%", textDecoration: "none" }}
                    >
                      <Box
                        p={4}
                        bg={surfaceCard}
                        borderWidth="1px"
                        borderColor={mutedLine}
                        borderRadius="lg"
                        w="full"
                        shadow="sm"
                        transition="all 0.2s ease"
                        _hover={{
                          shadow: "md",
                          borderColor: "purple.300",
                          transform: "translateY(-1px)",
                        }}
                      >
                        <Text fontWeight="bold" fontSize="md" color={linkColor}>
                          {result.title}
                        </Text>
                        <Text fontSize="xs" color={subtextColor} noOfLines={3} wordBreak="break-word" mt={1}>
                          {result.note}
                        </Text>
                      </Box>
                    </a>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          )}

      {!selectedCategory ? (
        <VStack spacing={6} align="stretch">
          {categories.length > 0 && (
            <DashboardCategoryPanel
              variant="browse"
              title="Your folders"
              description={`${categories.length} top-level folder${
                categories.length === 1 ? "" : "s"
              } — open one to manage notes or subfolders.`}
            >
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                {categories.map((category) => (
                  <DashboardFolderCard
                    key={category._id}
                    itemId={category._id}
                    variant="teal"
                    title={category.name}
                    borderColor={tableStyles.borderColor}
                    textColor={textColorCard}
                    inputBgColor={inputBg}
                    categoryRowBg={categoryRowBg}
                    categoryRowHoverBg={categoryRowHoverBg}
                    categoryIconBg={categoryIconBgRoot}
                    isEditingThisRow={
                      isEditing?.id === category._id &&
                      isEditing.type === "category"
                    }
                    editedTitle={editedCategoryName}
                    onEditedTitleChange={setEditedCategoryName}
                    onSelect={() => handleCategorySelect(category)}
                    onSaveEdit={() =>
                      handleCategoryUpdate(category._id, editedCategoryName)
                    }
                    onStartEdit={() => handleStartEdit(category, "category")}
                    onDelete={handleCategoryDelete}
                    deleteAriaLabel="Delete folder"
                    collaborationShared={!!category.collaborationShared}
                    collaborationCanInvite={!!category.collaborationCanInvite}
                    showShareButton={
                      !category.collaborationShared || !!category.collaborationCanInvite
                    }
                    onShareClick={() => {
                      setShareCategoryTarget(category);
                      shareFolderModal.onOpen();
                    }}
                  />
                ))}
              </SimpleGrid>
            </DashboardCategoryPanel>
          )}

          {categories.length === 0 && (
            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderStyle="dashed"
              borderColor={mutedLine}
              bg={pathStripBg}
            >
              <CardBody py={10} px={6} textAlign="center">
                <Icon as={FiEdit3} boxSize={10} color="purple.400" mx="auto" mb={3} />
                <Heading size="md" color={textColorCard} mb={2}>
                  No notes yet
                </Heading>
                <Text fontSize="sm" color={subtextColor} maxW="md" mx="auto">
                  Create a folder and add notes inside it. Content can be links or plain text.
                </Text>
              </CardBody>
            </Card>
          )}
        </VStack>
      ) : (
        <>
          <Card
            borderRadius="xl"
            borderWidth="1px"
            borderColor={mutedLine}
            bg={surfaceCard}
            boxShadow="sm"
            overflow="hidden"
          >
            <CardBody py={{ base: 4, md: 5 }}>
              <Flex
                justify="space-between"
                align="flex-start"
                gap={4}
                flexWrap="wrap"
              >
                <Box flex="1" minW={0}>
                  <Heading
                    as="h2"
                    size="lg"
                    color={headingNoteColor}
                    fontWeight="bold"
                    lineHeight="short"
                  >
                    {selectedCategory.name}
                  </Heading>
                  <Text fontSize="sm" color={subtextColor} mt={2} maxW="lg">
                    Subfolders and notes in this category.
                  </Text>
                </Box>
                <IconButton
                  icon={<CloseIcon />}
                  size="md"
                  colorScheme="red"
                  flexShrink={0}
                  borderRadius="full"
                  onClick={handleBackToCategories}
                  aria-label="Back to folders"
                  shadow="sm"
                />
              </Flex>
            </CardBody>
          </Card>

          <VStack spacing={6} align="stretch" w="full" mb={8}>
            {subCategories.length > 0 && (
              <DashboardCategoryPanel
                variant="nested"
                title="Subfolders"
                description={`${subCategories.length} subfolder${
                  subCategories.length === 1 ? "" : "s"
                } under “${selectedCategory.name}”.`}
              >
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                  {subCategories.map((subcategory) => (
                    <DashboardFolderCard
                      key={subcategory._id}
                      itemId={subcategory._id}
                      variant="blue"
                      title={subcategory.name}
                      borderColor={tableStyles.borderColor}
                      textColor={textColorCard}
                      inputBgColor={inputBg}
                      categoryRowBg={categoryRowBg}
                      categoryRowHoverBg={categoryRowHoverBg}
                      categoryIconBg={categoryIconBgNested}
                      isEditingThisRow={
                        isEditing?.id === subcategory._id &&
                        isEditing.type === "category"
                      }
                      editedTitle={editedCategoryName}
                      onEditedTitleChange={setEditedCategoryName}
                      onSelect={() => handleCategorySelect(subcategory)}
                      onSaveEdit={() =>
                        handleCategoryUpdate(subcategory._id, editedCategoryName)
                      }
                      onStartEdit={() =>
                        handleStartEdit(subcategory, "category")
                      }
                      onDelete={handleCategoryDelete}
                      deleteAriaLabel="Delete subfolder"
                    />
                  ))}
                </SimpleGrid>
              </DashboardCategoryPanel>
            )}

            {subNotes.length > 0 ? (
              <DashboardDataTable
                variant="emphasis"
                title="Notes in this folder"
                description={`${subNotes.length} note${
                  subNotes.length === 1 ? "" : "s"
                }.`}
                maxH={{ base: "none", md: "min(55vh, 480px)" }}
              >
                <Table
                  variant="simple"
                  size="sm"
                  colorScheme="gray"
                  sx={{ borderCollapse: "separate", borderSpacing: 0 }}
                >
                  <Thead
                    position="sticky"
                    top={0}
                    zIndex={1}
                    bg={tableStyles.tableHeaderBg}
                    backdropFilter="blur(8px)"
                    borderBottomWidth="1px"
                    borderColor={tableStyles.borderColor}
                  >
                    <Tr>
                      <Th {...tableStyles.thProps}>Title</Th>
                      <Th {...tableStyles.thProps}>Preview</Th>
                      <Th {...tableStyles.thProps} textAlign="right" w="120px">
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {subNotes.map((note) =>
                      isEditing?.id === note._id && isEditing.type === "note" ? (
                        <Tr key={note._id}>
                          <Td colSpan={3} py={4} bg={inputBg}>
                            <VStack align="stretch" spacing={3}>
                              <Input
                                value={editedNoteTitle}
                                onChange={(e) =>
                                  setEditedNoteTitle(e.target.value)
                                }
                                placeholder="Title"
                                bg={inputBg}
                              />
                              <Textarea
                                value={editedNote}
                                onChange={(e) => setEditedNote(e.target.value)}
                                placeholder="Note"
                                minH="100px"
                                bg={inputBg}
                              />
                              <HStack justify="flex-end">
                                <IconButton
                                  icon={<CheckIcon />}
                                  size="sm"
                                  onClick={() =>
                                    handleNoteUpdate(
                                      note._id,
                                      editedNote,
                                      editedNoteTitle
                                    )
                                  }
                                  colorScheme="green"
                                  aria-label="Confirm Edit"
                                />
                                <DeleteButtonWithConfirmation
                                  onDelete={handleNoteDelete}
                                  itemId={note._id}
                                  size="sm"
                                  colorScheme="red"
                                  ariaLabel="Delete Note"
                                />
                              </HStack>
                            </VStack>
                          </Td>
                        </Tr>
                      ) : (
                        <Tr key={note._id} sx={tableStyles.trHoverSx}>
                          <Td py={3} maxW="200px">
                            <Button
                              as="a"
                              href={note.note}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="link"
                              color={linkColor}
                              fontSize="sm"
                              fontWeight="semibold"
                              justifyContent="flex-start"
                              whiteSpace="normal"
                              textAlign="left"
                              h="auto"
                              py={1}
                            >
                              {note.title}
                            </Button>
                          </Td>
                          <Td py={3} fontSize="sm" color="gray.600" maxW="360px">
                            <Text noOfLines={3} wordBreak="break-word">
                              {note.note}
                            </Text>
                          </Td>
                          <Td textAlign="right" py={3}>
                            <HStack spacing={1} justify="flex-end">
                              <IconButton
                                icon={<EditIcon />}
                                size="sm"
                                onClick={() => handleStartEdit(note, "note")}
                                colorScheme="blue"
                                aria-label="Edit Note"
                              />
                              <DeleteButtonWithConfirmation
                                onDelete={handleNoteDelete}
                                itemId={note._id}
                                size="sm"
                                colorScheme="red"
                                ariaLabel="Delete Note"
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      )
                    )}
                  </Tbody>
                </Table>
              </DashboardDataTable>
            ) : (
              subCategories.length === 0 && (
                <Text color={subtextColor} fontSize="sm">
                  No subfolders or notes in this category yet.
                </Text>
              )
            )}
          </VStack>
        </>
      )}
      <Flex
        w="full"
        justify="space-between"
        gap={{ base: 6, md: 8 }}
        flexDirection={{ base: "column", lg: "row" }}
        align="stretch"
      >
          <DashboardActionShell
            kind="folder"
            badgeLabel="Structure"
            title="Add a folder"
          >
            <VStack spacing={4} align="stretch">
              <Input
                placeholder="Folder name"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                size="lg"
                borderRadius="md"
                bg={inputBg}
                shadow="sm"
              />
              <Button
                colorScheme="green"
                size="lg"
                borderRadius="md"
                onClick={() => handleCategoryCreate()}
                w="full"
              >
                Create folder
              </Button>
            </VStack>
          </DashboardActionShell>

          {selectedCategory && (
            <DashboardActionShell
              kind="note"
              badgeLabel="Content"
              title="Add a note"
            >
              <VStack spacing={4} align="stretch">
                <Input
                  placeholder="Note title"
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  size="lg"
                  borderRadius="md"
                  bgGradient={noteFieldGradient}
                  shadow="sm"
                />
                <Textarea
                  placeholder="Note content"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  size="lg"
                  borderRadius="md"
                  p={4}
                  minH="140px"
                  bgGradient={noteFieldGradient}
                  shadow="sm"
                  _focus={{ borderColor: "blue.300", shadow: "md" }}
                />
                <Button
                  colorScheme="blue"
                  size="lg"
                  borderRadius="md"
                  fontWeight="bold"
                  w="full"
                  onClick={handleNoteCreate}
                >
                  Save note
                </Button>
              </VStack>
            </DashboardActionShell>
          )}
        </Flex>
        </VStack>
      </Container>

      <NotesShareCollaborationModal
        isOpen={shareFolderModal.isOpen}
        onClose={() => {
          shareFolderModal.onClose();
          setShareCategoryTarget(null);
        }}
        titleLabel={shareNotesCollaborationTitle}
        kind="NOTE_CATEGORY"
        anchor={{ categoryAnchorId: shareNotesCategoryAnchorId }}
      />
    </Box>
  );
};

export default NoteComponent;
